import { useCallback, useEffect, useState } from 'react'
import { Button } from 'blaze-ui/Button/Button'
import { REACT_APP_READER_URL, REACT_APP_APPLE_CLIENT_ID, REACT_APP_APPLE_REDIRECT_URI } from 'env'
import { useQueryParams } from 'hooks/useQueryParams'
import { navigateToExternalUrl } from 'utils/location'
import { isWebview } from 'utils/browserUtils'
import { AppleIcon } from 'components/AppleIcon/AppleIcon'
import { IS_VM_REGISTRATION_KEY } from '../constants'
import Styles from './AppleOAuthButton.module.scss'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'

const AppleIconComponent = () => {
  return (
    <div className={Styles.appleIconWrapper}>
      <AppleIcon size={18} />
    </div>
  )
}

export const AppleOAuthButton = ({
  workspaceInviteToken,
  checkNewUser = false,
}: {
  workspaceInviteToken: string
  checkNewUser?: boolean
}) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAppleSdkReady, setIsAppleSdkReady] = useState(false)
  const { domain, redirect_to, promotion_code, campaigns } = useQueryParams()

  useEffect(() => {
    setIsAppleSdkReady(false)
    if (!REACT_APP_APPLE_CLIENT_ID || !REACT_APP_APPLE_REDIRECT_URI) return

    const initAppleSDK = () => {
      if (window.AppleID?.auth?.init) {
        window.AppleID.auth.init({
          clientId: REACT_APP_APPLE_CLIENT_ID,
          redirectURI: REACT_APP_APPLE_REDIRECT_URI,
          scope: 'name email',
          usePopup: false,
        })
        setIsAppleSdkReady(true)
      } else {
        setTimeout(initAppleSDK, 500)
      }
    }

    initAppleSDK()
  }, [])

  const loginOrSignup = useCallback(async () => {
    localStorage.setItem(IS_VM_REGISTRATION_KEY, 'true')
    setIsProcessing(true)

    if (window.AppleID?.auth?.signIn && isAppleSdkReady) {
      try {
        await window.AppleID.auth.signIn()
      } catch (error) {
        setIsProcessing(false)
        return
      }
    }

    const state = {
      redirect_to,
      workspace_invite_token: workspaceInviteToken,
      domain,
      promotion_code,
      check_new_user: checkNewUser,
      campaigns,
    }

    const encodedState = btoa(JSON.stringify(state))
    const authUrl = `${REACT_APP_READER_URL}/omniauth/apple?state=${encodedState}`

    if (window.top && window.top !== window) {
      window.top.location.href = authUrl
      return
    }

    navigateToExternalUrl(authUrl)
  }, [domain, redirect_to, workspaceInviteToken, promotion_code, checkNewUser, isAppleSdkReady, campaigns])

  if (isWebview(window.navigator.userAgent)) return null

  if (isOnboardingRedesign) {
    return (
      <Button
        variant="secondary"
        size="xl"
        fullWidth
        frontIcon={AppleIcon}
        useChildClass
        onPress={loginOrSignup}
        isDisabled={isProcessing}
        data-heap-id="continue-with-apple"
      >
        <div className={Styles.appleButtonLabel}>Continue with Apple</div>
      </Button>
    )
  }

  return (
    <div className={Styles.appleButtonWrapper}>
      <Button
        variant="primary"
        size="xl"
        fullWidth
        frontIcon={AppleIconComponent}
        useChildClass
        onPress={loginOrSignup}
        isDisabled={isProcessing}
        data-heap-id="continue-with-apple"
        className={Styles.appleButton}
        ignoreHover
      >
        <div className={Styles.appleOAuthButton}>Continue with Apple</div>
      </Button>
    </div>
  )
}
